// src/hooks/useImage.js
import { useState, useEffect, useMemo, useRef } from "react";
import { imageService } from "../services/imageService";

export const useNearViewport = (rootMargin = "500px") => {
  const ref = useRef(null);
  const [isNear, setIsNear] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node || isNear) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsNear(true);
          observer.disconnect();
        }
      },
      { rootMargin }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [isNear, rootMargin]);

  return [ref, isNear];
};

export const useImage = (docId) => {
  const imageUrl = docId ? imageService.getDisplayUrl(docId) : null;
  return { imageUrl, loading: false, error: null };
};

export const useImages = (docIds = []) => {
  const imagesById = useMemo(() => {
    const result = {};
    docIds.forEach((id) => {
      if (id) result[id] = imageService.getDisplayUrl(id);
    });
    return result;
  }, [docIds]);

  return { imagesById, loading: false, error: null };
};

export const useGallery = (excludedIds = []) => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const memoizedExcludedIds = useMemo(() => {
    return JSON.stringify(excludedIds);
  }, [excludedIds]);

  useEffect(() => {
    const fetchImages = async () => {
      setLoading(true);
      try {
        const idsArray = JSON.parse(memoizedExcludedIds);
        const data = await imageService.getGalleryImages(idsArray);
        setImages(data);
      } catch (err) {
        setError(err.message || "Failed to load gallery");
      } finally {
        setLoading(false);
      }
    };

    fetchImages();
  }, [memoizedExcludedIds]);

  return { images, loading, error };
};
